import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../store";
import { hydrateWorkspace } from "./workspacesSlice";

export interface Tab {
  id: string;
  /** Optional user-set label. When absent, the UI derives a label
   *  from the tab's ui-slice state (e.g. "XAUUSD · 1H"). */
  label?: string;
  /** When true, the tab is protected from deletion — its × close
   *  button is hidden and the "Close tab" context action is disabled,
   *  so it can't be closed by accident. Toggled from the tab context
   *  menu; persisted in the workspace so the lock survives reloads. */
  locked?: boolean;
}

/** Which pane a tab belongs to. 0 = left, 1 = right (split). Tabs
 *  default to pane 0; entering pane 1 requires an explicit user
 *  action ("Open in right pane" / "Move to right pane"). */
export type PaneIdx = 0 | 1;

interface TabsState {
  tabs: Tab[];
  /** Active tab in the left pane (and the only pane when no split). */
  activeTabId: string;
  /** Active tab in the right pane. `null` = no split (single-pane
   *  view). When non-null, must be in `paneByTabId` with value 1.
   *  Two readers care: App.tsx (which Charts get visibility) and
   *  TabBar (renders a second sub-bar). */
  splitTabId: string | null;
  /** Per-tab pane membership. Missing entry = pane 0 by default
   *  (avoids back-filling for legacy workspaces / tabs added before
   *  this field existed). Tabs in pane 1 surface in the right sub-
   *  bar; only one of them at a time is `splitTabId`. */
  paneByTabId: Record<string, PaneIdx>;
  /** Monotonically increasing counter used to mint new tab ids.
   *  Stored in state so the value persists across hot reloads. */
  nextSeq: number;
  /** Width fraction of the LEFT pane when split is active, 0..1.
   *  Default 0.5. The divider drag updates this; persisted in the
   *  workspace so the layout stays put across reloads. Clamped to
   *  [0.15, 0.85] so neither pane disappears. */
  splitRatio: number;
  /** Which pane the user is currently "focused" on. Header,
   *  Toolbar, Run Analysis, drawing tool selection — anything that
   *  targets "the" active tab — operate on this pane's active tab.
   *  Set by clicking a tab in its bar or clicking inside the
   *  chart pane itself. Always 0 when no split is active. */
  focusedPaneIdx: PaneIdx;
}

export const DEFAULT_TAB_ID = "tab_1";

const initialState: TabsState = {
  tabs: [{ id: DEFAULT_TAB_ID }],
  activeTabId: DEFAULT_TAB_ID,
  splitTabId: null,
  paneByTabId: { [DEFAULT_TAB_ID]: 0 },
  nextSeq: 2,
  splitRatio: 0.5,
  focusedPaneIdx: 0,
};

const SPLIT_RATIO_MIN = 0.15;
const SPLIT_RATIO_MAX = 0.85;

/** Convenience: which pane does a tab belong to? Undefined entries
 *  fall back to pane 0 (the default before this field was tracked).
 *  All read sites in reducers must go through this rather than
 *  indexing `state.paneByTabId[id]` directly, otherwise stray
 *  `undefined`s would slip into `=== 1` comparisons. */
function paneOf(state: TabsState, tabId: string): PaneIdx {
  return state.paneByTabId[tabId] === 1 ? 1 : 0;
}

/** Pick the first pane-1 tab in document order that's NOT `excludeId`.
 *  Used when the right pane's active tab leaves (closes or moves to
 *  left) — we promote the next pane-1 tab to be the right pane's
 *  active. Returns null if no other pane-1 tab exists. */
function firstPaneOneTab(state: TabsState, excludeId: string): string | null {
  for (const t of state.tabs) {
    if (t.id === excludeId) continue;
    if (paneOf(state, t.id) === 1) return t.id;
  }
  return null;
}

const tabsSlice = createSlice({
  name: "tabs",
  initialState,
  reducers: {
    /** Add a tab with an explicit id. Callers normally use the
     *  `createTab` thunk so the id is minted from `nextSeq`. */
    addTab(
      state,
      action: PayloadAction<{
        id: string;
        label?: string;
        activate?: boolean;
        /** Which pane the new tab joins. Defaults to 0 (left). The
         *  "+" button in the right sub-bar passes 1 so the new tab
         *  appears there and is set as the right pane's active. */
        pane?: PaneIdx;
      }>,
    ) {
      const { id, label, activate, pane } = action.payload;
      state.tabs.push({ id, label });
      state.paneByTabId[id] = pane === 1 ? 1 : 0;
      if (activate !== false) {
        if (pane === 1) {
          state.splitTabId = id;
        } else {
          state.activeTabId = id;
        }
      }
      const m = /^tab_(\d+)$/.exec(id);
      if (m) {
        const n = parseInt(m[1], 10);
        if (n >= state.nextSeq) state.nextSeq = n + 1;
      }
    },
    removeTab(state, action: PayloadAction<string>) {
      if (state.tabs.length <= 1) return;
      const id = action.payload;
      const idx = state.tabs.findIndex((t) => t.id === id);
      if (idx < 0) return;
      // Locked tabs are protected from deletion — the UI hides/disables
      // the close affordances, but guard here too so no code path (hotkey,
      // stray dispatch) can close a locked tab.
      if (state.tabs[idx].locked) return;
      const wasInPane = paneOf(state, id);
      state.tabs.splice(idx, 1);
      delete state.paneByTabId[id];

      if (wasInPane === 1) {
        // Right pane lost a tab. If it was the right pane's active,
        // promote another pane-1 tab; if pane 1 is now empty, close
        // the split.
        if (state.splitTabId === id) {
          state.splitTabId = firstPaneOneTab(state, id);
          if (state.splitTabId === null) {
            // Right pane vanished — focus back to left.
            state.focusedPaneIdx = 0;
          }
        }
        return;
      }

      // wasInPane === 0 (left).
      if (state.activeTabId === id) {
        // Find another pane-0 tab to take its place. Prefer the
        // neighbour at the same visual position.
        let replacement: string | null = null;
        for (let offset = 1; offset < state.tabs.length; offset++) {
          const candidates = [
            state.tabs[idx - offset],
            state.tabs[idx + offset - 1],
          ];
          for (const c of candidates) {
            if (c && paneOf(state, c.id) === 0) {
              replacement = c.id;
              break;
            }
          }
          if (replacement) break;
        }
        if (replacement) {
          state.activeTabId = replacement;
        } else {
          // No pane-0 tab left — promote the right pane's active to
          // fill the left, and close the split.
          if (state.splitTabId) {
            const promoted = state.splitTabId;
            state.paneByTabId[promoted] = 0;
            state.activeTabId = promoted;
            state.splitTabId = null;
            state.focusedPaneIdx = 0;
          }
        }
      }
    },
    setActiveTab(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (!state.tabs.some((t) => t.id === id)) return;
      const inPane = paneOf(state, id);
      if (inPane === 1) {
        // Clicking a right-pane tab in the right sub-bar just sets
        // it as the right pane's active. (The left sub-bar never
        // shows pane-1 tabs, so this branch only fires for clicks
        // from the right sub-bar.)
        state.splitTabId = id;
      } else {
        state.activeTabId = id;
      }
      // Clicking a tab also focuses its pane — Header / Toolbar /
      // analysis pick up the new focus immediately, so the user can
      // change TF / run analysis / use drawing tools on either side.
      state.focusedPaneIdx = inPane;
    },
    /** Set focused pane without changing the active tab in either
     *  pane. Dispatched by App.tsx when the user mousedowns inside
     *  a chart pane — gives the user a way to focus a pane without
     *  changing which tab is displayed in it. */
    setFocusedPane(state, action: PayloadAction<PaneIdx>) {
      const next = action.payload === 1 ? 1 : 0;
      // Refuse to focus pane 1 if there isn't one.
      if (next === 1 && state.splitTabId === null) return;
      state.focusedPaneIdx = next;
    },
    /** Open a tab in the right pane. Moves it from pane 0 → pane 1
     *  if needed, marks it as the right pane's active, and ensures
     *  the active tab in pane 0 is still valid. Passing `null`
     *  closes the split (right pane vanishes, its tabs collapse
     *  back into pane 0). */
    setSplitTab(state, action: PayloadAction<string | null>) {
      const next = action.payload;
      if (next === null) {
        // Close split: every pane-1 tab collapses back to pane 0.
        for (const t of state.tabs) {
          if (paneOf(state, t.id) === 1) {
            state.paneByTabId[t.id] = 0;
          }
        }
        state.splitTabId = null;
        // No right pane anymore — focus has nowhere to be but left.
        state.focusedPaneIdx = 0;
        return;
      }
      if (!state.tabs.some((t) => t.id === next)) return;
      // Don't let the same tab live in both panes simultaneously —
      // the user picked it for the right side, so move it there.
      state.paneByTabId[next] = 1;
      state.splitTabId = next;
      // Newly opening the right pane focuses it — matches user
      // intent ("I just chose this tab for the right side, work
      // there now").
      state.focusedPaneIdx = 1;
      // If the moved tab was also the left-pane active, pick a new
      // pane-0 active. If there are no pane-0 tabs left, this is the
      // degenerate "split open but left pane is empty" case — the
      // chart host won't render anything in the left slot. Avoid by
      // collapsing the split back.
      if (state.activeTabId === next) {
        const replacement = state.tabs.find(
          (t) => t.id !== next && paneOf(state, t.id) === 0,
        );
        if (replacement) {
          state.activeTabId = replacement.id;
        } else {
          // No other tabs in pane 0. Roll back: keep the tab as
          // active in pane 0 and don't open the split.
          state.paneByTabId[next] = 0;
          state.splitTabId = null;
        }
      }
    },
    /** Move a tab to a specific pane. Used by the context-menu
     *  actions "Move to right pane" / "Move to left pane". If the
     *  move would empty the right pane, the split closes
     *  automatically (via splitTabId fallthrough). */
    moveTabToPane(
      state,
      action: PayloadAction<{ tabId: string; pane: PaneIdx }>,
    ) {
      const { tabId, pane } = action.payload;
      if (!state.tabs.some((t) => t.id === tabId)) return;
      const currentPane = paneOf(state, tabId);
      if (currentPane === pane) return;
      state.paneByTabId[tabId] = pane;
      if (pane === 1) {
        // Moving into the right pane: make it the right active.
        // If the tab was the left active, pick a replacement.
        if (state.activeTabId === tabId) {
          const replacement = state.tabs.find(
            (t) => t.id !== tabId && paneOf(state, t.id) === 0,
          );
          if (replacement) {
            state.activeTabId = replacement.id;
          } else {
            // Same rollback as setSplitTab.
            state.paneByTabId[tabId] = 0;
            return;
          }
        }
        state.splitTabId = tabId;
      } else {
        // Moving back to left pane.
        if (state.splitTabId === tabId) {
          // Right pane's active is leaving — pick another pane-1 tab
          // to take over, or close the split if none.
          state.splitTabId = firstPaneOneTab(state, tabId);
        }
        // The left pane's active doesn't auto-change; user can click
        // the moved tab in the left sub-bar to focus it.
      }
    },
    setSplitRatio(state, action: PayloadAction<number>) {
      const r = action.payload;
      if (!Number.isFinite(r)) return;
      state.splitRatio = Math.max(
        SPLIT_RATIO_MIN,
        Math.min(SPLIT_RATIO_MAX, r),
      );
    },
    renameTab(state, action: PayloadAction<{ id: string; label: string }>) {
      const t = state.tabs.find((x) => x.id === action.payload.id);
      if (t) t.label = action.payload.label;
    },
    /** Toggle a tab's locked (protected-from-deletion) flag. */
    toggleTabLock(state, action: PayloadAction<string>) {
      const t = state.tabs.find((x) => x.id === action.payload);
      if (t) t.locked = !t.locked;
    },
    /** Drag-to-reorder. `beforeId === null` means "append at end".
     *  Caller specifies the moving tab id and the id it should land
     *  in front of — index-based reorder is fragile when the array
     *  shifts during the splice.
     *
     *  Reorder respects pane membership: dragging within the SAME
     *  pane (the left or right sub-bar) reorders within the global
     *  `tabs[]` array — which is fine because each sub-bar filters
     *  `tabs[]` by pane, so the visible order in each bar is just
     *  the filtered subset's order. Cross-pane drag (drop a left
     *  tab onto a right tab's position) is not exposed by the UI
     *  for now; if it happened it'd reorder the global list without
     *  touching `paneByTabId`, so the tab would stay in its origin
     *  pane. */
    reorderTabs(
      state,
      action: PayloadAction<{ movingId: string; beforeId: string | null }>,
    ) {
      const { movingId, beforeId } = action.payload;
      if (movingId === beforeId) return;
      const fromIdx = state.tabs.findIndex((t) => t.id === movingId);
      if (fromIdx < 0) return;
      const [moved] = state.tabs.splice(fromIdx, 1);
      if (beforeId === null) {
        state.tabs.push(moved);
        return;
      }
      const toIdx = state.tabs.findIndex((t) => t.id === beforeId);
      if (toIdx < 0) {
        // Target gone (race with removeTab) — fall back to end.
        state.tabs.push(moved);
        return;
      }
      state.tabs.splice(toIdx, 0, moved);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateWorkspace, (state, action) => {
      // Replace tabs list + activeTabId. Bump nextSeq above any
      // tab_N id the workspace contains so future createTab calls
      // don't collide with restored ids.
      state.tabs = action.payload.tabs.map((t) => ({
        id: t.id,
        ...(t.label != null ? { label: t.label } : {}),
        ...(t.locked ? { locked: true } : {}),
      }));
      // Ensure activeTabId points to an actual tab — fall back to
      // the first if the saved activeTabId was orphaned.
      const exists = state.tabs.some((t) => t.id === action.payload.activeTabId);
      state.activeTabId =
        exists && state.tabs.length > 0
          ? action.payload.activeTabId
          : (state.tabs[0]?.id ?? state.activeTabId);
      let maxSeq = state.nextSeq - 1;
      for (const tab of state.tabs) {
        const m = /^tab_(\d+)$/.exec(tab.id);
        if (m) {
          const n = parseInt(m[1], 10);
          if (n > maxSeq) maxSeq = n;
        }
      }
      state.nextSeq = maxSeq + 1;
      // Restore split view if the workspace payload carries one. The
      // payload may omit these fields (older saves) — fall back to
      // null/0.5 so old workspaces still load cleanly.
      const savedSplit = action.payload.splitTabId ?? null;
      const splitValid =
        savedSplit !== null &&
        savedSplit !== state.activeTabId &&
        state.tabs.some((t) => t.id === savedSplit);
      state.splitTabId = splitValid ? savedSplit : null;
      const savedRatio = action.payload.splitRatio;
      if (typeof savedRatio === "number" && Number.isFinite(savedRatio)) {
        state.splitRatio = Math.max(
          SPLIT_RATIO_MIN,
          Math.min(SPLIT_RATIO_MAX, savedRatio),
        );
      } else {
        state.splitRatio = 0.5;
      }
      // Restore pane membership. Two compat paths:
      //   1) New saves carry an explicit paneByTabId — use it,
      //      filtering to known tab ids.
      //   2) Old saves only carry splitTabId — every other tab is
      //      pane 0; the split tab is pane 1.
      const saved = action.payload.paneByTabId;
      const next: Record<string, PaneIdx> = {};
      for (const tab of state.tabs) {
        const v = saved?.[tab.id];
        if (v === 1 || v === 0) {
          next[tab.id] = v;
        } else if (state.splitTabId === tab.id) {
          next[tab.id] = 1;
        } else {
          next[tab.id] = 0;
        }
      }
      state.paneByTabId = next;
      // Sanity: splitTabId must be in pane 1.
      if (state.splitTabId && next[state.splitTabId] !== 1) {
        state.splitTabId = null;
      }
    });
  },
});

export const {
  addTab,
  removeTab,
  setActiveTab,
  renameTab,
  toggleTabLock,
  reorderTabs,
  setSplitTab,
  setSplitRatio,
  setFocusedPane,
  moveTabToPane,
} = tabsSlice.actions;

/** Mint a fresh id off the slice's counter, dispatch addTab, and
 *  return the new id so the caller can seed per-tab state in other
 *  slices (uiSlice, ohlcSlice, etc).
 *
 *  `pane` defaults to 0 (left). The right sub-bar's "+" passes 1
 *  to land the new tab on the right side directly. */
export function createTab(label?: string, pane: PaneIdx = 0) {
  return (dispatch: AppDispatch, getState: () => RootState): string => {
    const id = `tab_${getState().tabs.nextSeq}`;
    dispatch(addTab({ id, label, pane }));
    return id;
  };
}

export default tabsSlice.reducer;
